/**
 * Validation utility functions for QR settings
 */

/**
 * Helper function to check if a value is a valid integer
 * @param value - The value to check
 * @returns true if the value is an integer, false otherwise
 */
const isInteger = (value: number | string): boolean => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return !isNaN(num) && Number.isInteger(num);
};

/**
 * Helper function to check if a value is within a specified range (inclusive)
 * @param value - The value to check
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns true if the value is within range, false otherwise
 */
const isInRange = (value: number | string, min: number, max: number): boolean => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Validates QR Code TTL (Time To Live) hours
 * @param value - The TTL value to validate (in hours)
 * @returns Error message string if invalid, null if valid
 *
 * Validation rules:
 * - Must be a non-negative integer
 * - Must be 0 (never expires) or between 1 and 8760 hours (1 year max)
 * - Empty values are invalid
 * - Non-numeric values are invalid
 */
export const validateTtlHours = (value: number | string): string | null => {
  // Check for empty values
  if (value === '' || value === null || value === undefined) {
    return 'QR kod geçerlilik süresi boş bırakılamaz';
  }

  // Convert to number for validation
  const num = typeof value === 'string' ? parseFloat(value) : value;

  // Check if it's a valid number
  if (isNaN(num)) {
    return 'Geçersiz değer. Lütfen sayısal bir değer giriniz';
  }

  // Check if it's an integer
  if (!isInteger(value)) {
    return 'Geçersiz değer. Lütfen tam sayı giriniz';
  }

  // Check if it's non-negative
  if (num < 0) {
    return 'Geçersiz değer. Negatif değer girilemez';
  }

  // Check if it's 0 or between 1 and 8760 (1 year max)
  if (num === 0) {
    return null; // 0 is valid (never expires)
  }

  if (!isInRange(num, 1, 8760)) {
    return '0 ile 8760 arasında bir değer giriniz (maksimum 1 yıl)';
  }

  return null;
};

/**
 * Validates location range in meters
 * @param value - The location range value to validate (in meters)
 * @returns Error message string if invalid, null if valid
 *
 * Validation rules:
 * - Must be a positive integer
 * - Must be between 50 and 500 meters (inclusive)
 * - Empty values are invalid
 * - Non-numeric values are invalid
 */
export const validateLocationRange = (value: number | string): string | null => {
  // Check for empty values
  if (value === '' || value === null || value === undefined) {
    return 'Konum aralığı boş bırakılamaz';
  }

  // Convert to number for validation
  const num = typeof value === 'string' ? parseFloat(value) : value;

  // Check if it's a valid number
  if (isNaN(num)) {
    return 'Geçersiz değer. Lütfen sayısal bir değer giriniz';
  }

  // Check if it's an integer
  if (!isInteger(value)) {
    return 'Geçersiz değer. Lütfen tam sayı giriniz';
  }

  // Check if it's positive
  if (num <= 0) {
    return 'Geçersiz değer. Pozitif bir değer giriniz';
  }

  // Check if it's within the valid range
  if (!isInRange(num, 50, 500)) {
    return '50 ile 500 metre arasında bir değer giriniz';
  }

  return null;
};
